// Pure helpers for building the trip payload and validating wizard steps
import { buildStopsPayload } from './tripDialogUtils';

const mapUplifts = (uplifts) => uplifts.length > 0 ? uplifts.map(u => ({
  name: u.name || '',
  amount: parseFloat(u.amount) || 0,
  date_from: u.applies_to === 'specific_dates' ? (u.date_from || null) : null,
  date_to: u.applies_to === 'specific_dates' ? (u.date_to || null) : null,
  description: u.description || ''
})) : null;

export const buildTripPayload = (formData, { contracts, dayRates, uplifts, deductions, ukCrossings }) => {
  const hasMultipleCrossings = ukCrossings.length > 0;

  const payload = {
    ...formData,
    has_multiple_uk_crossings: hasMultipleCrossings,
    foreign_ports: formData.foreign_ports
      ? formData.foreign_ports.split(',').map(p => p.trim()).filter(Boolean)
      : [],
    uk_water_crossings: ukCrossings.length > 0 ? ukCrossings : null,
    leave_type: formData.leave_type || null,
    adjustment_type: formData.adjustment_type || null,
    adjustment_description: formData.adjustment_description || null,
    adjustment_amount: formData.adjustment_amount ? parseFloat(formData.adjustment_amount) : null,
    actual_gbp_received: formData.currency !== 'GBP' && formData.actual_gbp_received
      ? parseFloat(formData.actual_gbp_received)
      : null,
    deductions: deductions.length > 0 ? deductions.map(d => ({
      ...d,
      amount: parseFloat(d.amount) || 0,
      applies_to_month: d.applies_to_month || null
    })) : null
  };

  if (formData.trip_type !== 'wage_adjustment') {
    const contract = formData.contract_id ? contracts.find(c => c.id === formData.contract_id) : null;
    const payType = contract ? (contract.contract_type === 'salaried' ? 'salaried' : 'day_rate') : formData.pay_type;
    payload.pay_type = payType;

    if (payType === 'salaried') {
      payload.monthly_salary = contract ? (contract.monthly_base || null) : (formData.monthly_salary ? parseFloat(formData.monthly_salary) : null);
      payload.sea_uplift_rate = contract ? (contract.daily_uplift || null) : (formData.sea_uplift_rate ? parseFloat(formData.sea_uplift_rate) : null);
      payload.base_day_rate = null;
      payload.uplifts = mapUplifts(uplifts);
      payload.day_rates = null;
      payload.travel_uplift_rate = contract?.travel_day_rate ? parseFloat(contract.travel_day_rate) : (formData.has_separate_travel_rate && formData.travel_uplift_rate ? parseFloat(formData.travel_uplift_rate) : null);
      payload.hotel_uplift_rate = contract?.hotel_day_rate ? parseFloat(contract.hotel_day_rate) : (formData.has_separate_hotel_rate && formData.hotel_uplift_rate ? parseFloat(formData.hotel_uplift_rate) : null);
      payload.travel_day_rate = null;
      payload.hotel_day_rate = null;
      payload.has_separate_travel_rate = !!(payload.travel_uplift_rate);
      payload.has_separate_hotel_rate = !!(payload.hotel_uplift_rate);
    } else {
      payload.monthly_salary = null;
      payload.sea_uplift_rate = null;
      payload.base_day_rate = null;
      payload.uplifts = mapUplifts(uplifts);
      if (contract && contract.daily_rate) {
        const startDate = payload.date_left_uk || formData.date_joined_vessel || '';
        const endDate = payload.date_returned_uk || formData.date_left_vessel || '';
        payload.day_rates = [{ date_from: startDate, date_to: endDate, rate_type: 'standard', daily_rate: parseFloat(contract.daily_rate) || 0, description: '' }];
      } else {
        payload.day_rates = dayRates.length > 0 ? dayRates.map(r => ({ ...r, daily_rate: parseFloat(r.daily_rate) || 0 })) : null;
      }
      payload.travel_day_rate = contract?.travel_day_rate ? parseFloat(contract.travel_day_rate) : (formData.has_separate_travel_rate && formData.travel_day_rate ? parseFloat(formData.travel_day_rate) : null);
      payload.hotel_day_rate = contract?.hotel_day_rate ? parseFloat(contract.hotel_day_rate) : (formData.has_separate_hotel_rate && formData.hotel_day_rate ? parseFloat(formData.hotel_day_rate) : null);
      payload.travel_uplift_rate = null;
      payload.hotel_uplift_rate = null;
      payload.has_separate_travel_rate = !!(payload.travel_day_rate);
      payload.has_separate_hotel_rate = !!(payload.hotel_day_rate);
    }
  }

  return payload;
};

// Apply UK date fallbacks based on trip type, uk_waters_status and pre/post travel
export const applyDateFallbacks = (payload, formData) => {
  if (formData.trip_type === 'work_trip') {
    const status = formData.uk_waters_status || 'outside';

    Object.assign(payload, buildStopsPayload(formData));

    if (formData.pre_had_travel && formData.pre_travel_abroad && formData.pre_departure_date) {
      payload.date_left_uk = formData.pre_departure_date;
    }
    if (formData.post_had_travel && formData.post_travel_abroad && formData.post_return_date) {
      payload.date_returned_uk = formData.post_return_date;
    }

    if (status === 'outside') {
      if (!payload.date_left_uk && !formData.no_uk_departure) {
        payload.date_left_uk = formData.date_left_uk || formData.date_joined_vessel;
      }
      if (!payload.date_returned_uk && !formData.no_uk_return) {
        payload.date_returned_uk = formData.date_returned_uk || formData.date_left_vessel;
      }
    } else if (status === 'uk_only') {
      if (!payload.date_left_uk) {
        payload.date_left_uk = formData.date_joined_vessel;
      }
      if (!payload.date_returned_uk) {
        payload.date_returned_uk = formData.date_left_vessel;
      }
    } else if (status === 'sailed_from_uk') {
      if (!payload.date_returned_uk && !formData.no_uk_return) {
        payload.date_returned_uk = formData.date_returned_uk || formData.date_left_vessel;
      }
    } else if (status === 'sailed_into_uk') {
      if (!payload.date_left_uk && !formData.no_uk_departure) {
        payload.date_left_uk = formData.date_left_uk || formData.date_joined_vessel;
      }
    }
    // 'both' — user provides both dates explicitly, no fallback needed
  }

  if (['base_work_foreign', 'course_foreign', 'visa_run'].includes(formData.trip_type)) {
    Object.assign(payload, buildStopsPayload(formData));

    if (formData.pre_had_travel && formData.pre_travel_abroad && formData.pre_departure_date) {
      payload.date_left_uk = formData.pre_departure_date;
    }
    if (formData.post_had_travel && formData.post_travel_abroad && formData.post_return_date) {
      payload.date_returned_uk = formData.post_return_date;
    }
  }

  return payload;
};

// Find existing trips whose dates overlap the new trip's dates
export const checkForOverlaps = (existingTrips, currentTrip, newTripStart, newTripEnd, isContinuousStart, isContinuousEnd) => {
  if (!newTripStart || !newTripEnd) return [];
  if (isContinuousStart || isContinuousEnd) return [];

  const newStart = new Date(newTripStart);
  const newEnd = new Date(newTripEnd);

  return existingTrips.filter(existingTrip => {
    if (currentTrip && existingTrip.id === currentTrip.id) return false;
    if (existingTrip.no_uk_departure || existingTrip.no_uk_return) return false;

    const existingStart = existingTrip.date_left_uk || existingTrip.date_joined_vessel;
    const existingEnd = existingTrip.date_returned_uk || existingTrip.date_left_vessel;
    if (!existingStart || !existingEnd) return false;

    const exStart = new Date(existingStart);
    const exEnd = new Date(existingEnd);
    return newStart <= exEnd && newEnd >= exStart;
  });
};

// Wizard step validation
export const canGoNextStep = (currentStep, formData, flags) => {
  const { isContractLinkable, isWorkTrip, isHotelStay, isUkHotelStay, isCourse, isBaseWork, isVisaRun, isPaidLeave } = flags;
  if (currentStep === 1) {
    if (isContractLinkable) return true; // Contract is optional
    if (isWorkTrip) return true;
    if (isHotelStay || isUkHotelStay) return formData.hotel_name?.trim();
    if (isCourse) return formData.course_name?.trim();
    if (isBaseWork) return formData.base_name?.trim();
    return true;
  }
  if (currentStep === 2) {
    if (isContractLinkable) {
      if (isWorkTrip) return formData.vessel_name?.trim();
      if (isHotelStay || isUkHotelStay) return formData.hotel_name?.trim() && formData.date_left_uk && formData.date_returned_uk;
      if (isCourse) return formData.course_name?.trim() && formData.date_left_uk && formData.date_returned_uk;
      if (isBaseWork) return formData.base_name?.trim() && formData.date_left_uk && formData.date_returned_uk;
      if (isVisaRun) return formData.date_left_uk && formData.date_returned_uk;
      if (isPaidLeave) return formData.date_left_uk && formData.date_returned_uk;
      return true;
    }
    return true;
  }
  if (currentStep === 3) {
    if (isWorkTrip) return formData.date_joined_vessel && formData.date_left_vessel;
    return true;
  }
  return true;
};
